import { TradingCategoriesEnum } from '@spa/api';

import { PositionInterface } from '../../../../interfaces';
import { PermissionsEnum, PortalPermissionsEnum } from '../../../../enums';

import {
    ClientAccessRightsTypesEnum,
    TradingAccountTypesEnum
} from '../../../../../entries/panel/enums';

import { PortalPermissionsPositionsEnumHelper } from './portal-permissions-positions-enum.helper';

export class PermissionsEnumHelper {
    private constructor() {
    }

    static getDefaultPortalMainPermissions(isLegalRepresentative: boolean = false): string[] {
        const permissions = [
            PortalPermissionsEnum.ViewDocuments,
            PortalPermissionsEnum.UploadNewDocuments,
            PortalPermissionsEnum.UpdateDocumentDetails,
            PortalPermissionsEnum.DownloadOrPreviewDocument,
            PortalPermissionsEnum.DeleteDocuments,
            PortalPermissionsEnum.ViewOwnProfile,
            PortalPermissionsEnum.ViewMessageCenter
        ];

        if (isLegalRepresentative) {
            permissions.push(PortalPermissionsEnum.ViewCompanyProfile, PortalPermissionsEnum.EditCompanyProfile);
        }

        return permissions;
    }

    static getPortalManagerialRightsPermissions(clientTradingCategory: number): string[] {
        if (clientTradingCategory !== TradingCategoriesEnum.Retail) {
            return [
                PortalPermissionsEnum.CashAccountsCreateNew,
                PortalPermissionsEnum.CashAccountsDelete,
                PortalPermissionsEnum.AddBankAccount,
                PortalPermissionsEnum.ViewBankAccount,
                PortalPermissionsEnum.EditBankAccount
            ];
        }

        return [
            PortalPermissionsEnum.AddBankAccount,
            PortalPermissionsEnum.ViewBankAccount,
            PortalPermissionsEnum.EditBankAccount
        ];
    }

    static getPortalCashOperationsOperationsPermissions(): string[] {
        return [
            PortalPermissionsEnum.CreateNewWithdrawals,
            PortalPermissionsEnum.CreateNewDeposits,
            PortalPermissionsEnum.NewInternalTransfer,
            PortalPermissionsEnum.UpdatePaymentDetails,
            PortalPermissionsEnum.CashOperationsViewTransactionHistoryAndPendingWithdrawals
        ];
    }

    static getPortalReportsPermissions(): string[] {
        return [
            PortalPermissionsEnum.ViewTradingAccountStatementsAndReports,
            PortalPermissionsEnum.ViewGroupStatementsAndReports,
        ];
    }

    static getPortalMdsPermissions(): string[] {
        return [
            PortalPermissionsEnum.SubscriptionManagementViewAssignedFeeds,
            PortalPermissionsEnum.SubscriptionManagementViewRevenue,
            PortalPermissionsEnum.SubscriptionManagementViewSubscriptionPrice,
            PortalPermissionsEnum.SubscriptionManagementAssignFeedsToUsers,
            PortalPermissionsEnum.SubscriptionManagementEditAssignedFeeds,
            PortalPermissionsEnum.SubscriptionManagementViewFeedPrice,
            PortalPermissionsEnum.SubscriptionManagementViewMarkups,
            PortalPermissionsEnum.SubscriptionManagementSetMarkups,
            PortalPermissionsEnum.SubscriptionManagementViewEventHistory,
            PortalPermissionsEnum.SubscriptionManagementViewMarketDataUsageReports,
            PortalPermissionsEnum.SubscriptionManagementViewAvailableFeeds
        ];
    }

    static getPortalUsersPermissions(): string[] {
        return [
            PortalPermissionsEnum.AddNewClientUser,
            PortalPermissionsEnum.ViewClientUserDetails,
            PortalPermissionsEnum.EditClientUserDetails,
            PortalPermissionsEnum.DeleteClientUser
        ];
    }

    static getPortalCurrencyExchangePermissions(): string[] {
        return [
            PortalPermissionsEnum.CurrencyExchangeExchangeCurrency,
            PortalPermissionsEnum.CurrencyExchangeViewCurrencyTransactions 
        ];
    }

    static getPortalFundPortalPermissions(showAll = false): string[] {
        if (showAll) {
            return [
                PortalPermissionsEnum.FPManageAccounts,
                PortalPermissionsEnum.FPDocumentsUploadNew,
                PortalPermissionsEnum.FPDocumentsView,
                PortalPermissionsEnum.FPDocumentsUpdate,
                PortalPermissionsEnum.FPDocumentsDownloadPreview,
                PortalPermissionsEnum.FPDocumentsDelete
            ];
        }

        return [
            PortalPermissionsEnum.FPManageAccounts,
        ];
    }

    static getPortalMainPermissionPosition(rightsType: number, permissionString): PositionInterface {
        switch (rightsType) {
            case ClientAccessRightsTypesEnum.ManagerialRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalManagerialRightsPermissionPosition(permissionString);
            case ClientAccessRightsTypesEnum.CashOperationsRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalCashOperationsPermissionPosition(permissionString);
            case ClientAccessRightsTypesEnum.ReportsRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalReportsPermissionPosition(permissionString);
            case ClientAccessRightsTypesEnum.MdsRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalMdsPermissionPosition(permissionString);
            case ClientAccessRightsTypesEnum.UsersRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalUsersPermissionPosition(permissionString);
            case ClientAccessRightsTypesEnum.CurrencyExchangeRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalCurrencyExchangePosition(permissionString);
            case ClientAccessRightsTypesEnum.FundPortalRights:
                return PortalPermissionsPositionsEnumHelper
                    .getPortalFundPortalPosition(permissionString);
        }
    }

    static getPortalTradingAccountPermissions(): string[] {
        return [
            PortalPermissionsEnum.TradingAccountsView,
            PortalPermissionsEnum.TradingAccountsEdit,
            PortalPermissionsEnum.TradingAccountsViewReports,
            PortalPermissionsEnum.TradingAccountsExecuteTrades,
            PortalPermissionsEnum.TradingAccountsCreateNewDeposits,
            PortalPermissionsEnum.TradingAccountsCreateNewWithdrawals,
            PortalPermissionsEnum.TradingAccountsCreateNewTransfers,
            PortalPermissionsEnum.TradingAccountsExchangeCurrency,
            PortalPermissionsEnum.FPManageAccounts,
        ];
    }

    static getPortalCashAccountPermissions(): string[] {
        return [
            PortalPermissionsEnum.CashAccountsView,
            PortalPermissionsEnum.CashAccountsEdit,
            PortalPermissionsEnum.CashAccountsViewReports,
            PortalPermissionsEnum.CashAccountsCreateNewDeposits,
            PortalPermissionsEnum.CashAccountsCreateNewWithdrawals,
            PortalPermissionsEnum.CashAccountsCreateNewTransfers,
            PortalPermissionsEnum.TradingAccountsCreateNew,
            PortalPermissionsEnum.TradingAccountsDelete,
            PortalPermissionsEnum.FPManageAccounts,
        ];
    }

    static getPortalSupervisedPermissions(): string[] {
        return [
            PortalPermissionsEnum.DeleteSupervisedUser,
            PortalPermissionsEnum.EditSupervisedUserDetails,
            PortalPermissionsEnum.ViewSupervisedUserDetails
        ];
    }

    static getPortalSupervisedPermissionPosition(permissionString: string): PositionInterface {
        return PortalPermissionsPositionsEnumHelper.getSupervisedPermissionPosition(permissionString);
    }

    static getPortalAccountPermissionPosition(accountType: number, permissionString: string): PositionInterface {
        if (accountType !== TradingAccountTypesEnum.Cash) {
            return PortalPermissionsPositionsEnumHelper.getTradingAccountAccessPermissionsPosition(permissionString);
        }

        return PortalPermissionsPositionsEnumHelper.getCashAccountAccessPermissionsPosition(permissionString);
    }

    static getDetailsViewPermissionsByAccountType(type: number): string[] {
        if (type !== TradingAccountTypesEnum.Cash) {
            return [
                PermissionsEnum.CrmTradingAccountsCreateNew,
                PermissionsEnum.CrmTradingAccountsViewDetails,
                PermissionsEnum.CrmTradingAccountsUpdateDetails
            ];
        }

        return [
            PermissionsEnum.CrmCashAccountsCreateNew,
            PermissionsEnum.CrmCashAccountsViewDetails,
            PermissionsEnum.CrmCashAccountsUpdateDetails
        ];
    }

    static getFeePlansViewPermissions(type: number): string[] {
        if (type === TradingAccountTypesEnum.Cash) {
            return [
                PermissionsEnum.CrmCashAccountsViewFeePlans,
                PermissionsEnum.CrmCashAccountsAddFeePlans,
                PermissionsEnum.CrmCashAccountsUpdateFeePlans,
                PermissionsEnum.CrmCashAccountsDeleteFeePlans
            ];
        }

        return [
            PermissionsEnum.CrmTradingAccountsViewFeePlans,
            PermissionsEnum.CrmTradingAccountsAddFeePlans,
            PermissionsEnum.CrmTradingAccountsUpdateFeePlans,
            PermissionsEnum.CrmTradingAccountsDeleteFeePlans
        ];
    }

    static getCurrentPositionsViewPermissions(): string[] {
        return [
            PermissionsEnum.CrmTradingAccountsViewCurrentPositions
        ];
    }

    static getAccountsViewPermissionsByAccountType(type: number): string[] {
        if (type !== TradingAccountTypesEnum.Cash) {
            return [
                PermissionsEnum.CrmTradingAccountsCreateNew,
                PermissionsEnum.CrmTradingAccountsViewDetails,
                PermissionsEnum.CrmTradingAccountsUpdateDetails,
            ];
        }

        return [
            PermissionsEnum.CrmCashAccountsCreateNew,
            PermissionsEnum.CrmCashAccountsViewDetails,
            PermissionsEnum.CrmCashAccountsUpdateDetails
        ];
    }
}
